import { useState, useRef, useEffect } from 'react';
import { JULKISIVU_RAKENNEOSA_OPTIONS } from '../../../assets/julkisivuRakenneosaOptions';

const CUSTOM = '__custom__';

const ICON_STYLE = { color: '#495057', fontSize: '1.2rem', flexShrink: 0, lineHeight: 1 };

const DisplayValue = ({ value, style, onClick }) => (
  <div
    style={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%', cursor: 'pointer' }}
    onClick={onClick}
  >
    <span style={{ flex: 1, fontSize: style?.fontSize, lineHeight: 1.35, wordBreak: 'break-word' }}>
      {value}
    </span>
    <span style={ICON_STYLE}>▾</span>
  </div>
);

export default function RakenneosaSelect({ options, value, onChange, onCommit, onBlur, onKeyDown, style, autoFocus, placeholder }) {
  const opts = options ?? JULKISIVU_RAKENNEOSA_OPTIONS;
  const isCustom = value !== '' && !opts.includes(value);
  const [showCustom, setShowCustom] = useState(isCustom);
  const [editing, setEditing] = useState(!value || !!autoFocus);
  const selectRef = useRef(null);
  const openOnMount = useRef(false);

  useEffect(() => {
    if (autoFocus && !showCustom && selectRef.current?.showPicker) {
      try { selectRef.current.showPicker(); } catch {}
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectCallbackRef = (el) => {
    selectRef.current = el;
    if (el && openOnMount.current) {
      openOnMount.current = false;
      try { el.showPicker(); } catch {}
    }
  };

  const openDropdown = () => {
    openOnMount.current = true;
    setShowCustom(false);
    setEditing(true);
  };

  const handleSelectChange = (e) => {
    const v = e.target.value;
    if (v === CUSTOM) {
      setShowCustom(true);
      setEditing(true);
      onChange('');
    } else {
      setShowCustom(false);
      setEditing(false);
      onChange(v);
      onCommit?.(v);
    }
  };

  const handleSelectBlur = () => {
    // If value is still custom text (not in opts), restore custom display mode
    if (value && !opts.includes(value)) setShowCustom(true);
    setEditing(false);
    onBlur?.();
  };

  // Custom text — display mode
  if (showCustom && !editing && value) {
    return <DisplayValue value={value} style={style} onClick={openDropdown} />;
  }

  // Custom text — input mode
  if (showCustom && editing) {
    return (
      <div style={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
        <input
          autoFocus
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={() => { setEditing(false); onBlur?.(); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? 'Kirjoita…'}
          style={{ ...style, flex: 1, minWidth: 0 }}
        />
        <button
          type="button"
          tabIndex={-1}
          title="Palaa listaan"
          onClick={() => { setShowCustom(false); setEditing(true); onChange(''); }}
          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1, ...ICON_STYLE }}
        >▾</button>
      </div>
    );
  }

  // Predefined value — display mode
  if (!editing && opts.includes(value)) {
    return <DisplayValue value={value} style={style} onClick={openDropdown} />;
  }

  // Select dropdown
  return (
    <select
      ref={selectCallbackRef}
      autoFocus={autoFocus}
      value={opts.includes(value) ? value : ''}
      onChange={handleSelectChange}
      onBlur={handleSelectBlur}
      onKeyDown={onKeyDown}
      style={{ ...style }}
      className={opts.includes(value) ? undefined : 'rakenneosa-placeholder'}
    >
      <option value="" disabled>{placeholder ?? 'Valitse…'}</option>
      {opts.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
      <option value={CUSTOM}>Muu (kirjoita)…</option>
    </select>
  );
}
